/**
 * What a diet's candidates registered to sit.
 *
 * Two tables in one document, because the exams office reads both off the same
 * fact and printing them separately invites them to disagree:
 *
 *   by subject     one line per subject with the number registered. This is
 *                  what question papers are counted from.
 *   by candidate   one line per candidate with the subjects they registered.
 *                  The roll people are checked in against.
 *
 * Candidates with NOTHING registered are listed rather than dropped, and
 * flagged. A course exam is registered when the candidate finalises, so an
 * empty row means they paid and never finished — and finding those before the
 * day rather than on it is most of what this report is for.
 *
 * Same house style and the same dependency-free approach as
 * exportExamApplications.ts: CSV with a UTF-8 BOM for the spreadsheet, and a
 * styled print window for the PDF where the browser's own "Save as PDF" does
 * the writing.
 */

export type DietCourseTally = {
  code?: string | null;
  name?: string | null;
  candidates: number;
};

/**
 * One centre's share of the diet.
 *
 * `centre` is null for the group holding candidates who have no centre set —
 * nobody can be sent a paper for them, so they are reported rather than
 * quietly left out of the totals.
 */
export type DietCourseCentre = {
  centre?: string | null;
  state?: string | null;
  candidates: number;
  with_courses: number;
  registrations: number;
  courses: DietCourseTally[];
};

export type DietCourseCandidate = {
  sn: number;
  name?: string | null;
  membership_no?: string | null;
  exam_no?: string | null;
  status?: string | null;
  center?: string | null;
  center_state?: string | null;
  course_count: number;
  courses: { code?: string | null; name?: string | null }[];
};

export type DietCoursesReport = {
  diet?: { id?: string; title?: string | null } | null;
  exam?: { id?: string; code?: string | null; title?: string | null } | null;
  totals: {
    candidates: number;
    with_courses: number;
    without_courses: number;
    subjects: number;
    registrations: number;
    /** Centres in use — the no-centre group is not counted as one. */
    centres?: number;
  };
  by_course: DietCourseTally[];
  /** Optional: an API that predates the per-centre breakdown omits it. */
  by_centre?: DietCourseCentre[];
  candidates: DietCourseCandidate[];
};

/* ── Shared ──────────────────────────────────────────────────────────────── */

function stamp() {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    printed: now.toLocaleString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
    file: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
  };
}

const slug = (text: string) =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "report";

function examLabel(report: DietCoursesReport) {
  const e = report.exam;
  return [e?.code, e?.title].filter(Boolean).join(" — ") || "Exam";
}

function dietLabel(report: DietCoursesReport) {
  return report.diet?.title || "Diet";
}

function filename(report: DietCoursesReport, extension: string) {
  return `registered-courses-${slug(examLabel(report))}-${slug(dietLabel(report))}-${stamp().file}.${extension}`;
}

/** "TPC1, TPC2, TPC4" — the codes, which is how the office refers to them. */
const codeList = (c: DietCourseCandidate) =>
  c.courses.map((x) => x.code || x.name || "?").join(", ");

const prettyStatus = (s?: string | null) =>
  String(s ?? "").replace(/_/g, " ") || "—";

/** A centre's heading, or what to call the group that has none. */
const centreLabel = (c: DietCourseCentre) =>
  c.centre || "No centre set";

const centreSubLabel = (c: DietCourseCentre) =>
  c.centre ? c.state || "" : "Nobody can be sent a paper for these candidates";

/* ── Spreadsheet ─────────────────────────────────────────────────────────── */

const csvCell = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;
const csvLine = (cells: unknown[]) => cells.map(csvCell).join(",");

export function exportDietCoursesSpreadsheet(report: DietCoursesReport) {
  const { printed } = stamp();
  const t = report.totals;

  const lines: string[] = [
    csvLine([`NIQS — Registered Courses — ${examLabel(report)}`]),
    csvLine([`Diet: ${dietLabel(report)}`]),
    csvLine([`Candidates: ${t.candidates}`]),
    csvLine([`Registered at least one subject: ${t.with_courses}`]),
    csvLine([`Registered nothing: ${t.without_courses}`]),
    csvLine([`Subjects in use: ${t.subjects}`]),
    csvLine([`Total registrations: ${t.registrations}`]),
    csvLine([`Taken on: ${printed}`]),
    "",
    csvLine(["BY SUBJECT"]),
    csvLine(["Code", "Subject", "Candidates registered"]),
    ...report.by_course.map((c) => csvLine([c.code, c.name, c.candidates])),
    "",
    // One FLAT table rather than a block per centre: a sheet is read by
    // sorting and pivoting, and repeating the centre on every line is what
    // makes that possible. The PDF below groups it instead, because a printed
    // page is read by eye.
    csvLine(["BY CENTRE AND SUBJECT"]),
    csvLine(["Centre", "State", "Code", "Subject", "Candidates registered"]),
    ...(report.by_centre ?? []).flatMap((ct) =>
      ct.courses.length
        ? ct.courses.map((c) =>
            csvLine([centreLabel(ct), ct.state, c.code, c.name, c.candidates]),
          )
        : // A centre whose candidates have registered nothing still belongs on
          // the dispatch list, saying so.
          [csvLine([centreLabel(ct), ct.state, "", "Nothing registered", 0])],
    ),
    "",
    csvLine(["CENTRE TOTALS"]),
    csvLine(["Centre", "State", "Candidates", "Registered a subject", "Registrations"]),
    ...(report.by_centre ?? []).map((ct) =>
      csvLine([
        centreLabel(ct),
        ct.state,
        ct.candidates,
        ct.with_courses,
        ct.registrations,
      ]),
    ),
    "",
    csvLine(["BY CANDIDATE"]),
    // One subject per COLUMN would need a column per subject and change width
    // with the diet; the codes in one cell keep the sheet pivotable on centre
    // and sortable on the count, which is what it gets used for.
    csvLine([
      "S/N",
      "Candidate",
      "Membership No",
      "Exam No",
      "Status",
      "Centre",
      "State",
      "Subjects",
      "Registered",
    ]),
    ...report.candidates.map((c) =>
      csvLine([
        c.sn,
        c.name,
        c.membership_no,
        c.exam_no,
        prettyStatus(c.status),
        c.center,
        c.center_state,
        c.course_count,
        codeList(c),
      ]),
    ),
  ];

  const blob = new Blob(["﻿" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename(report, "csv");
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick — Safari cancels the download if the URL dies
  // before it has read it.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ── PDF (via the browser's print dialog) ────────────────────────────────── */

const esc = (v: unknown) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export function exportDietCoursesPdf(report: DietCoursesReport) {
  const { printed } = stamp();
  const w = window.open("", "_blank", "width=1100,height=800");
  if (!w) {
    alert("Allow pop-ups for this site to print this report.");
    return;
  }

  const t = report.totals;

  const subjectRows = report.by_course.length
    ? report.by_course
        .map(
          (c) => `<tr>
            <td class="mono"><b>${esc(c.code ?? "—")}</b></td>
            <td>${esc(c.name ?? "—")}</td>
            <td class="num">${c.candidates}</td>
          </tr>`,
        )
        .join("")
    : `<tr><td class="empty" colspan="3">No subject has been registered in this diet.</td></tr>`;

  /* Grouped, not flat — a printed dispatch list is read one centre at a time,
     and repeating the centre name on every subject line would be noise on
     paper even though it is exactly what the spreadsheet needs. */
  const centreBlocks = (report.by_centre ?? [])
    .map((ct) => {
      const rows = ct.courses.length
        ? ct.courses
            .map(
              (c) => `<tr>
                <td class="mono"><b>${esc(c.code ?? "—")}</b></td>
                <td>${esc(c.name ?? "—")}</td>
                <td class="num">${c.candidates}</td>
              </tr>`,
            )
            .join("")
        : `<tr><td class="empty" colspan="3">No subject registered by anyone at this centre.</td></tr>`;

      return `<div class="centre${ct.centre ? "" : " centre-none"}">
        <div class="centre-head">
          <div>
            <span class="centre-name">${esc(centreLabel(ct))}</span>
            ${centreSubLabel(ct) ? `<span class="centre-sub">${esc(centreSubLabel(ct))}</span>` : ""}
          </div>
          <div class="centre-figs">
            <b>${ct.candidates}</b> candidate${ct.candidates === 1 ? "" : "s"}
            &middot; <b>${ct.registrations}</b> registration${ct.registrations === 1 ? "" : "s"}
            ${
              ct.candidates - ct.with_courses > 0
                ? `<span class="warn"> &middot; ${ct.candidates - ct.with_courses} registered nothing</span>`
                : ""
            }
          </div>
        </div>
        <table>
          <thead><tr><th>Code</th><th>Subject</th><th>Candidates</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
    })
    .join("");

  const candidateRows = report.candidates.length
    ? report.candidates
        .map(
          (c) => `<tr${c.course_count === 0 ? ' class="flagged"' : ""}>
            <td class="sn">${c.sn}</td>
            <td>
              <b>${esc(c.name ?? "—")}</b>
              ${c.membership_no ? `<div class="sub">${esc(c.membership_no)}</div>` : ""}
            </td>
            <td class="mono">${esc(c.exam_no ?? "—")}</td>
            <td>
              ${esc(c.center ?? "—")}
              ${c.center_state ? `<div class="sub">${esc(c.center_state)}</div>` : ""}
            </td>
            <td class="status">${esc(prettyStatus(c.status))}</td>
            <td class="num">${c.course_count}</td>
            <td>${
              c.courses.length
                ? `<ul>${c.courses
                    .map(
                      (s) =>
                        `<li><b>${esc(s.code ?? "")}</b>${s.name ? ` ${esc(s.name)}` : ""}</li>`,
                    )
                    .join("")}</ul>`
                : `<span class="warn">Nothing registered</span>`
            }</td>
          </tr>`,
        )
        .join("")
    : `<tr><td class="empty" colspan="7">No candidates in this diet.</td></tr>`;

  w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${esc(filename(report, "pdf").replace(/\.pdf$/, ""))}</title>
  <style>
    @page{size:A4 portrait;margin:12mm}
    *{box-sizing:border-box;margin:0;padding:0;font-family:'Segoe UI',Arial,sans-serif}
    body{color:#111;font-size:10px}
    .head{border-bottom:2px solid #111;padding-bottom:12px;margin-bottom:14px}
    .org{font-size:9px;font-weight:900;letter-spacing:.22em;text-transform:uppercase;color:#888}
    h1{font-size:17px;font-weight:900;margin-top:4px}
    h2{font-size:11px;font-weight:900;letter-spacing:.14em;text-transform:uppercase;margin:18px 0 7px;color:#111}
    .sub-head{font-size:10px;color:#666;margin-top:5px;line-height:1.6}
    .sub-head b{color:#111}
    table{width:100%;border-collapse:collapse}
    th{background:#111;color:#fff;font-size:8px;font-weight:900;letter-spacing:.08em;text-transform:uppercase;text-align:left;padding:7px 6px}
    td{padding:6px;border-bottom:1px solid #eee;vertical-align:top}
    td.sn{width:28px;text-align:right;font-weight:900;color:#666}
    td.num{width:52px;text-align:right;font-weight:900}
    td.mono{font-family:'Courier New',monospace;white-space:nowrap}
    td.status{text-transform:capitalize;white-space:nowrap;color:#666}
    td.empty{text-align:center;color:#999;padding:24px}
    .sub{font-size:8px;color:#888;margin-top:1px}
    /* A candidate who registered nothing is the row the office is looking for,
       so it is marked on the page rather than left to be counted by eye. */
    tr.flagged td{background:#fff8f8}
    .warn{color:#b42318;font-weight:800}
    .note{font-size:9px;color:#888;margin:-3px 0 8px}
    /* One centre's dispatch, kept whole on the page — a subject list split
       across a break reads as belonging to the centre overleaf. */
    .centre{margin-bottom:12px;page-break-inside:avoid}
    .centre-head{display:flex;justify-content:space-between;align-items:baseline;gap:10px;
      border-bottom:1px solid #111;padding-bottom:4px;margin-bottom:4px}
    .centre-name{font-size:11px;font-weight:900}
    .centre-sub{font-size:9px;color:#888;margin-left:6px}
    .centre-figs{font-size:9px;color:#666;white-space:nowrap}
    .centre-none .centre-name{color:#b42318}
    ul{margin:0;padding-left:12px}
    li{margin-bottom:2px;line-height:1.35}
    li:last-child{margin-bottom:0}
    tr{page-break-inside:avoid}
    thead{display:table-header-group}
    .foot{margin-top:14px;padding-top:8px;border-top:1px solid #eee;font-size:8px;color:#aaa;font-weight:800;letter-spacing:.12em;text-transform:uppercase}
  </style>
</head>
<body>
  <div class="head">
    <div class="org">Nigerian Institute of Quantity Surveyors</div>
    <h1>Registered Courses — ${esc(examLabel(report))}</h1>
    <div class="sub-head">
      Diet: <b>${esc(dietLabel(report))}</b> &middot;
      <b>${t.candidates}</b> candidate${t.candidates === 1 ? "" : "s"} &middot;
      <b>${t.subjects}</b> subject${t.subjects === 1 ? "" : "s"} &middot;
      <b>${t.registrations}</b> registration${t.registrations === 1 ? "" : "s"}
      ${t.centres ? ` &middot; <b>${t.centres}</b> centre${t.centres === 1 ? "" : "s"}` : ""}
      ${
        t.without_courses
          ? `<br><span class="warn">${t.without_courses} candidate${
              t.without_courses === 1 ? " has" : "s have"
            } registered nothing</span>`
          : ""
      }
      <br>Taken on ${esc(printed)}
    </div>
  </div>

  <h2>By subject</h2>
  <table>
    <thead>
      <tr><th>Code</th><th>Subject</th><th>Candidates</th></tr>
    </thead>
    <tbody>${subjectRows}</tbody>
  </table>

  ${
    centreBlocks
      ? `<h2>By centre</h2>
         <p class="note">What each centre has to be sent. Subjects in the order the pile is counted in — most needed first.</p>
         ${centreBlocks}`
      : ""
  }

  <h2>By candidate</h2>
  <table>
    <thead>
      <tr>
        <th>S/N</th>
        <th>Candidate</th>
        <th>Exam No</th>
        <th>Centre</th>
        <th>Status</th>
        <th>No.</th>
        <th>Subjects registered</th>
      </tr>
    </thead>
    <tbody>${candidateRows}</tbody>
  </table>

  <div class="foot">NIQS Portal &middot; Registered Courses</div>
  <script>
    (function () {
      var printed = false;
      function go() { if (printed) return; printed = true; window.print(); }
      window.onload = go;
      setTimeout(go, 6000);
    })();
  </script>
</body>
</html>`);
  w.document.close();
}
